import { writable, derived } from "svelte/store";
import { browser } from "$app/environment";
import { authStore } from "./authStore";
import type { Readable } from "svelte/store";
import type { ListMetadata, GroupedLists, User } from "$lib/types";

export class ListHistoryManager {
  public static readonly STORAGE_KEY = "listHistory";
  public static readonly ANONYMOUS_USER = "anonymous";

  /**
   * Gets user identifier for storing lists
   */
  static getUserIdentifier(user: User | undefined): string {
    if (!user) return this.ANONYMOUS_USER;
    return user.id || user.email || this.ANONYMOUS_USER;
  }

  /**
   * Merges new lists with existing ones, preserving order and removing duplicates
   */
  static mergeLists(existing: ListMetadata[], incoming: ListMetadata[]): ListMetadata[] {
    const listMap = new Map<string, ListMetadata>();

    incoming.forEach((list) => {
      listMap.set(list.slug, list);
    });
    existing.forEach((list) => {
      listMap.set(list.slug, list);
    });

    return Array.from(listMap.values()).sort(
      (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
    );
  }

  /**
   * Attaches user ID to historical lists
   */
  private static attachUserIdToHistory(history: ListMetadata[]): ListMetadata[] {
    const currentUser = authStore.getUser();
    const userID = this.getUserIdentifier(currentUser);

    return history.map((list) => (list.userId ? list : { ...list, userID }));
  }

  /**
   * Saves list history to localStorage
   */
  static saveToLocalStorage(lists: ListMetadata[]): void {
    if (!browser) return;

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(lists));
    } catch (error) {
      console.error("Error saving list history:", error);
    }
  }

  /**
   * Gets time-based group label for a list
   */
  static getTimeGroupLabel(date: Date): string {
    if (isNaN(date.getTime())) return "Other";

    const timeFrames = this.getTimeFrames();

    if (date >= timeFrames.today) return "Today";
    if (date >= timeFrames.yesterday) return "Yesterday";
    if (date >= timeFrames.lastWeek) return "Last week";
    if (date >= timeFrames.lastMonth) return "Last month";

    return date.toLocaleString("default", { month: "long", year: "numeric" });
  }

  /**
   * Gets relevant time frames for grouping
   */
  private static getTimeFrames() {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    return { today, yesterday, lastWeek, lastMonth };
  }
}

// Create the base store
const { subscribe, update } = writable<ListMetadata[]>([]);

// Create and export the main store
export const listHistoryStore = {
  subscribe,

  addList(list: ListMetadata) {
    update((lists) => {
      const userID = ListHistoryManager.getUserIdentifier(authStore.getUser());

      const newList = {
        ...list,
        userID,
      } as ListMetadata;

      const updatedLists = [newList, ...lists.filter((l) => l.slug !== list.slug)];

      // ListHistoryManager.saveToLocalStorage(updatedLists);
      return updatedLists;
    });
  },

  updateList(slug: string, updates: Partial<ListMetadata>) {
    update((lists) => {
      const userID = ListHistoryManager.getUserIdentifier(authStore.getUser());

      const updatedLists = lists.map((list) =>
        list.slug === slug && (String(list.userId) === userID || !list.userId) ? { ...list, ...updates, userID } : list,
      );

      // ListHistoryManager.saveToLocalStorage(updatedLists);
      return updatedLists;
    });
  },

  clearHistory() {
    update((lists) => {
      const userID = ListHistoryManager.getUserIdentifier(authStore.getUser());
      const updatedLists = lists.filter((list) => String(list.userId) !== userID);

      // ListHistoryManager.saveToLocalStorage(updatedLists);
      return updatedLists;
    });
  },

  removeList(slug: string) {
    update((lists) => {
      if (!authStore.getUser()) return lists;

      const updatedLists = lists.filter((l) => l.slug !== slug);
      // ListHistoryManager.saveToLocalStorage(updatedLists);
      return updatedLists;
    });
  },

  migrateAnonymousHistory(user: User) {
    update((lists) => {
      const userID = ListHistoryManager.getUserIdentifier(user);

      const updatedLists = lists.map((list) =>
        String(list.userId) === ListHistoryManager.ANONYMOUS_USER ? { ...list, userID } : list,
      );

      // ListHistoryManager.saveToLocalStorage(updatedLists);
      return updatedLists;
    });
  },

  setLists(lists: ListMetadata[]) {
    update((existingLists) => {
      logger.log("Setting lists:", lists);
      const userID = ListHistoryManager.getUserIdentifier(authStore.getUser());

      const newLists = lists.map((list) => ({
        ...list,
        userId: Number(userID) || list.userId,
        createdAt: list.createdAt instanceof Date ? list.createdAt : new Date(list.createdAt),
        updatedAt: list.updatedAt instanceof Date ? list.updatedAt : new Date(list.updatedAt),
      }));

      const updatedLists = [...newLists];
      return updatedLists;
    });
  },

  appendLists(newLists: ListMetadata[]) {
    update((existingLists) => {
      const userID = ListHistoryManager.getUserIdentifier(authStore.getUser());

      // Prepare new lists with user ID
      const processedNewLists = newLists.map((list) => ({
        ...list,
        userID,
      }));

      // Merge existing and new lists
      const mergedLists = ListHistoryManager.mergeLists(existingLists, processedNewLists);

      // ListHistoryManager.saveToLocalStorage(mergedLists);
      return mergedLists;
    });
  },

  getListsForCurrentUser(): ListMetadata[] {
    const userID = ListHistoryManager.getUserIdentifier(authStore.getUser());
    let lists: ListMetadata[] = [];

    const unsubscribe = subscribe((currentLists) => {
      lists = currentLists.filter((list) => String(list.userId) === userID);
    });

    unsubscribe();
    return lists;
  },

  getLatestList(): ListMetadata | undefined {
    const userID = ListHistoryManager.getUserIdentifier(authStore.getUser());
    let latestList: ListMetadata | undefined;

    const unsubscribe = subscribe((currentLists) => {
      const userLists = currentLists.filter((list) => String(list.userId) === userID);
      latestList = userLists.length > 0 ? userLists[0] : undefined;
    });

    unsubscribe();
    return latestList;
  },
};

export function groupLists(lists: ListMetadata[]): Record<string, ListMetadata[]> {
  const groups: Record<string, ListMetadata[]> = {};

  // First sort lists by createdAt in descending order
  const sortedLists = [...lists].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  sortedLists.forEach((list) => {
    const groupLabel = ListHistoryManager.getTimeGroupLabel(new Date(list.createdAt));
    if (!groups[groupLabel]) {
      groups[groupLabel] = [];
    }
    groups[groupLabel].push(list);
  });

  return groups;
}
